import React, { useState, useRef, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import PersonIcon from '@mui/icons-material/Person';
import { usePlayer } from '../../lib/PlayerContext';
import UserMenu from '../auth/UserMenu';

interface HeaderProps {
  onToggleSidebar: () => void;
  onOpenAuthModal: () => void;
}

interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

export function Header({ onToggleSidebar, onOpenAuthModal }: HeaderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const player = usePlayer();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [showQuickSettings, setShowQuickSettings] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(() => {
    return localStorage.getItem('reducedMotion') === 'true';
  });
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    return localStorage.getItem('notificationsEnabled') !== 'false';
  });
  const searchRef = useRef<HTMLInputElement>(null);
  const quickSettingsRef = useRef<HTMLDivElement>(null);

  // Detect OS for keyboard shortcut display
  const isMac = typeof window !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const keyboardShortcut = isMac ? '⌘K' : 'Ctrl+K';

  // Initialize search query from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryParam = searchParams.get('q');
    if (queryParam && location.pathname === '/search') {
      setSearchQuery(queryParam);
    } else if (location.pathname !== '/search') {
      // Clear search query when not on search page
      setSearchQuery('');
    }
  }, [location]);

  // Apply reduced motion setting on component mount
  useEffect(() => {
    if (reducedMotion) {
      document.body.classList.add('reduce-motion');
    } else {
      document.body.classList.remove('reduce-motion');
    }
  }, [reducedMotion]);

  // Generate breadcrumbs based on current path
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Home', path: '/', icon: HomeIcon }
    ];

    if (pathSegments.length === 0) return breadcrumbs;

    const routeMap: Record<string, { label: string; icon?: React.ComponentType<{ className?: string }> }> = {
      'library': { label: 'Library', icon: LibraryMusicIcon },
      'search': { label: 'Search', icon: SearchIcon },
      'profile': { label: 'Profile', icon: PersonIcon },
      'about': { label: 'About' },
      'help': { label: 'Help' },
      'changelog': { label: 'Changelog' },
      'forartists': { label: 'For Artists' },
    };

    pathSegments.forEach((segment, index) => {
      const route = routeMap[segment];
      if (route) {
        const path = '/' + pathSegments.slice(0, index + 1).join('/');
        breadcrumbs.push({
          label: route.label,
          path: path,
          icon: route.icon
        });
      }
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Handle global search with actual search functionality
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery(''); // Clear search after navigation
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + K for search focus
      if (((!isMac && e.ctrlKey) || (isMac && e.metaKey)) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMac]);

  // Handle clicking outside Quick Settings
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quickSettingsRef.current && !quickSettingsRef.current.contains(event.target as Node)) {
        setShowQuickSettings(false);
      }
    };

    if (showQuickSettings) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showQuickSettings]);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    player.setVolume(newVolume);
  };

  const handleReducedMotionToggle = () => {
    const newValue = !reducedMotion;
    setReducedMotion(newValue);
    localStorage.setItem('reducedMotion', String(newValue));
    
    // Apply reduced motion styles
    if (newValue) {
      document.body.classList.add('reduce-motion');
    } else {
      document.body.classList.remove('reduce-motion');
    }
  };

  const handleNotificationsToggle = () => {
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    localStorage.setItem('notificationsEnabled', String(newValue));
  };

  return (
    <header className="bg-surface-dark/95 backdrop-blur-lg border-b border-surface-border sticky top-0 z-30 elevation-2">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        
        {/* Left section - Mobile menu & Breadcrumbs */}
        <div className="flex items-center flex-1 min-w-0">
          {/* Mobile menu button */}
          <button
            onClick={onToggleSidebar}
            className="p-2 text-slate-grey hover:text-pearl-white hover:bg-white/5 rounded-lg transition-all duration-200 mr-4 md:hidden clickable"
            aria-label="Toggle menu"
          >
            <MenuIcon />
          </button>

          {/* Breadcrumbs */}
          <nav className="hidden sm:flex items-center space-x-2 min-w-0 flex-1">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.path || crumb.label}>
                {index > 0 && (
                  <ChevronRightIcon className="text-text-muted w-4 h-4 flex-shrink-0" />
                )}
                <div className="flex items-center space-x-1 min-w-0">
                  {crumb.icon && <crumb.icon className="w-4 h-4 text-text-muted flex-shrink-0" />}
                  {crumb.path && index < breadcrumbs.length - 1 ? (
                    <Link
                      to={crumb.path}
                      className="text-text-secondary hover:text-pearl-white transition-colors text-sm truncate"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-pearl-white text-sm font-medium truncate">
                      {crumb.label}
                    </span>
                  )}
                </div>
              </React.Fragment>
            ))}
          </nav>
        </div>

        {/* Center section - Global Search */}
        <div className="flex-1 max-w-xl mx-4 hidden md:block">
          <form onSubmit={handleSearch} className="relative">
            <div className={`relative transition-all duration-200 ${
              searchFocused ? 'scale-105' : ''
            }`}>
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-5 h-5" />
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder={`Search songs, artists, albums... (${keyboardShortcut})`}
                className={`w-full pl-10 pr-4 py-2.5 bg-surface-raised border border-surface-border rounded-full text-pearl-white placeholder-text-muted transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-naia-pink focus:border-transparent ${
                  searchFocused ? 'elevation-2' : 'elevation-1'
                }`}
              />
            </div>
          </form>
        </div>

        {/* Right section - Quick Settings & User Menu */}
        <div className="flex items-center space-x-2 flex-shrink-0">
          
          {/* Quick Settings */}
          <div className="relative" ref={quickSettingsRef}>
            <button
              onClick={() => setShowQuickSettings(!showQuickSettings)}
              className="p-2 text-slate-grey hover:text-pearl-white hover:bg-white/5 rounded-lg transition-all duration-200 clickable"
              aria-label="Quick settings"
            >
              <SettingsIcon />
            </button>
            
            {showQuickSettings && (
              <div className="absolute right-0 top-12 w-80 bg-surface-raised border border-surface-border rounded-lg p-4 z-50 elevation-4 animate-fade-in">
                <h3 className="text-pearl-white font-medium mb-4">Quick Settings</h3>
                
                {/* Volume Control */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-text-secondary text-sm">Volume</label>
                    <span className="text-text-muted text-sm">{Math.round(player.volume * 100)}%</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <VolumeUpIcon className="text-text-muted w-4 h-4" />
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={player.volume}
                      onChange={handleVolumeChange}
                      className="flex-1"
                    />
                  </div>
                </div>
                
                {/* Reduced Motion */}
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary text-sm">Reduced Motion</span>
                    <button
                      onClick={handleReducedMotionToggle}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full ${
                        reducedMotion ? 'bg-naia-pink' : 'bg-surface-border'
                      } ${reducedMotion ? '' : 'transition-colors'}`}
                    >
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-pearl-white ${
                          reducedMotion ? 'translate-x-5' : 'translate-x-1'
                        } ${reducedMotion ? '' : 'transition-transform'}`}
                      />
                    </button>
                  </div>
                </div>
                
                {/* Notifications */}
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary text-sm">Notifications</span>
                  <button 
                    onClick={handleNotificationsToggle}
                    className={`p-1 rounded transition-colors ${
                      notificationsEnabled ? 'text-naia-pink' : 'text-text-muted hover:text-pearl-white'
                    }`}
                  >
                    <NotificationsIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile search button */}
          <button
            onClick={() => navigate('/search')}
            className="p-2 text-slate-grey hover:text-pearl-white hover:bg-white/5 rounded-lg transition-all duration-200 md:hidden clickable"
            aria-label="Search"
          >
            <SearchIcon />
          </button>

          {/* User Menu */}
          <UserMenu onOpenAuthModal={onOpenAuthModal} />
        </div>
      </div>

      {/* Mobile search bar (when on search page) */}
      {location.pathname === '/search' && (
        <div className="px-4 pb-4 md:hidden">
          <form onSubmit={handleSearch} className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-5 h-5" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search songs, artists, albums..."
              className="w-full pl-10 pr-4 py-2.5 bg-surface-raised border border-surface-border rounded-full text-pearl-white placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-naia-pink focus:border-transparent elevation-1"
            />
          </form>
        </div>
      )}
    </header>
  );
} 